<script setup lang="ts">
import { computed, ref } from 'vue';
import { trackInsightEvent } from '@/api/tracking';
import type { IconTypes } from '@stockmarkteam/donguri-ui';
import SegmentedControl from '@/components/common/molecules/segmented-control.vue';
import InsightAxisPanel from '@/components/home/insights/insight-axis-panel.vue';
import {
  useInsightId,
  useInsightPageName,
} from '@/components/home/insights/use-insight-tracking';
import { insightAxisPresentations } from '@/components/insights/insight-display';
import type {
  InsightAxis,
  InsightAxisType,
  InsightPayload,
} from '@/types/insights';
import { useLayoutVariant } from '@/utils/composables/useLayoutVariant';
import { featureFlags } from '@/featureFlags';

interface Props {
  payload?: InsightPayload;
  hasSetupPersonalNewsOrFavoriteThemes: boolean;
  hasPersonalNewsArticles: boolean;
  hasFeaturedArticles: boolean;
  layout?: 'inline' | 'split';
}

const props = withDefaults(defineProps<Props>(), {
  layout: 'inline',
});

type TabId = 'all' | InsightAxisType;

type Tab = {
  id: TabId;
  text: string;
  icon?: IconTypes;
  axis?: InsightAxis;
};

const tabs = computed<Tab[]>(() => [
  {
    id: 'all',
    text: 'すべて',
  },
  {
    id: 'personal',
    text: insightAxisPresentations.personal.label,
    icon: insightAxisPresentations.personal.icon,
    axis: props.payload?.personal_interest,
  },
  {
    id: 'organization',
    text: insightAxisPresentations.organization.label,
    icon: insightAxisPresentations.organization.icon,
    axis: props.payload?.organization_interest,
  },
  {
    id: 'outside',
    text: insightAxisPresentations.outside.label,
    icon: insightAxisPresentations.outside.icon,
    axis: props.payload?.outside_interest,
  },
]);

const selectedTab = ref<TabId>('all');
const insightId = useInsightId();
const pageName = useInsightPageName();
const layoutVariant = useLayoutVariant();

const isTopicHintPair = featureFlags.ANDEV_10719_INSIGHT_TOPIC_HINT_PAIR;

const segments = computed(() =>
  tabs.value.map(tab => ({ label: tab.text, value: tab.id, icon: tab.icon })),
);

const onSelectTab = (tabId: TabId | undefined) => {
  const tab = tabs.value.find(t => t.id === tabId);
  if (tab === undefined) return;
  selectedTab.value = tab.id;
  if (insightId === null) return;
  if (tab.id !== 'all' && tab.axis === undefined) return;
  const axis =
    tab.id === 'all'
      ? { name: 'all' as const }
      : tab.axis?.generation_status === 'processed'
        ? { id: tab.axis.id, name: tab.id }
        : { name: tab.id };
  trackInsightEvent('insight_axis_view', {
    page: { name: pageName },
    insight: { id: insightId },
    axis,
    layout_variant: layoutVariant,
  });
};

const visibleAxes = computed<InsightAxis[]>(() => {
  const source =
    selectedTab.value === 'all'
      ? tabs.value
      : tabs.value.filter(tab => tab.id === selectedTab.value);
  return source
    .map(tab => tab.axis)
    .filter((axis): axis is InsightAxis => axis !== undefined);
});

// パネルは provide 時に軸 id を固定するため、公開されて id が決まったら作り直す
const axisPanelKey = (axis: InsightAxis) =>
  axis.generation_status === 'processed'
    ? `${axis.axis}-${axis.id}`
    : axis.axis;
</script>

<template>
  <div
    class="insights-tabs"
    :class="{
      split: layout === 'split',
      paired: isTopicHintPair,
    }"
  >
    <SegmentedControl
      size="medium"
      :segments="segments"
      :model-value="selectedTab"
      @update:model-value="onSelectTab"
    />
    <div class="tab-content">
      <template v-for="axis in visibleAxes" :key="axisPanelKey(axis)">
        <InsightAxisPanel
          :axis="axis"
          :has-setup-personal-news-or-favorite-themes="
            hasSetupPersonalNewsOrFavoriteThemes
          "
          :has-personal-news-articles="hasPersonalNewsArticles"
          :has-featured-articles="hasFeaturedArticles"
        />
      </template>
      <div v-if="$slots.footer" class="tab-footer">
        <slot name="footer" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.insights-tabs {
  .tab-content {
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin-top: 16px;
    margin-bottom: 8px;
  }

  &.paired .tab-content {
    gap: 16px;
  }

  .tab-footer {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &.split .tab-content {
    margin-bottom: 0;
  }

  // ペアのカードはそれぞれ白背景の枠を持つため、外側の白い枠は旧レイアウトだけに付ける。
  &.split:not(.paired) .tab-content {
    padding: 24px;
    border: 1px solid $color-gray400;
    border-radius: 4px;
    background-color: $color-white;
  }
}
</style>
